import React, { useState, useEffect } from 'react';
import { Create, SimpleForm, TextInput, Edit, required, EditButton, useNotify, useRefresh, Confirm } from 'react-admin';
import { Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import SortableTable from './components/SortableTable';
import { httpClient, publicBase } from './dataProvider';

const DeleteCategoryButton = ({ record }) => {
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleClick = () => setOpen(true);
    const handleDialogClose = () => setOpen(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            const url = `${publicBase}/course_categories/${record.id}`;
            await httpClient(url, { method: 'DELETE' });
            notify('Category deleted successfully', { type: 'success' });
            refresh();
            window.location.reload();
        } catch (error) {
            console.error('Error deleting category:', error);
            notify('Failed to delete category', { type: 'error' });
        } finally {
            setLoading(false);
            setOpen(false);
        }
    };

    return (
        <>
            <Button
                onClick={handleClick}
                color="error"
                startIcon={<DeleteIcon />}
            >
                Delete
            </Button>
            <Confirm
                isOpen={open}
                loading={loading}
                title="Delete Category"
                content="Are you sure you want to delete this category? This action cannot be undone."
                onConfirm={handleConfirm}
                onClose={handleDialogClose}
            />
        </>
    );
};

export const CourseCategoryList = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const url = `${publicBase}/course_categories?end=1000`; // Fetch all for client-side sort
        httpClient(url)
            .then(({ json }) => {
                setCategories(json || []);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching course categories:', error);
                setLoading(false);
            });
    }, []);

    const columns = [
        { key: 'name', header: 'Name' },
        {
            key: 'actions',
            header: 'Actions',
            sortable: false,
            render: (record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <EditButton record={record} resource="course_categories" />
                    <DeleteCategoryButton record={record} />
                </div>
            )
        }
    ];

    if (loading) return <p>Loading...</p>;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2>Course Categories</h2>
                <Link
                    to="/course_categories/create"
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}
                >
                    Create
                </Link>
            </div>
            <SortableTable data={categories} columns={columns} />
        </div>
    );
};

export const CourseCategoryCreate = props => (
    <Create {...props} title="Create a Course Category" redirect="list">
        <SimpleForm>
            <TextInput source="name" validate={required()} />
        </SimpleForm>
    </Create>
);

export const CourseCategoryEdit = props => (
    <Edit {...props} title="Edit Course Category">
        <SimpleForm>
            <TextInput source="name" validate={required()} />
        </SimpleForm>
    </Edit>
);
